// Shared pickup date/time picker for catering order pages. Exposes
// window.ChezGPickupPicker.mount(opts) which attaches a calendar + 30-min
// slot picker to an existing readonly input, populates the input value with
// an ISO string ("YYYY-MM-DDTHH:MM"), and fires opts.onChange on selection.
//
// opts: {
//   input:     HTMLInputElement,           // the input to drive (id is kept)
//   locale:    'en' | 'fr',
//   pickup:    {                           // shape from /api/menu
//     windows:         [{days_of_week:int[], start_time, end_time}],
//     operating_hours: [{day_of_week, open_time, close_time}],
//     buffer_hours:    number,
//     start_date:      'YYYY-MM-DD' | null,
//     end_date:        'YYYY-MM-DD' | null,
//   },
//   onChange?: (value: string | '') => void,
// }
//
// The picker falls back to operating_hours (open + 1h → close) when
// pickup.windows is empty. Dates outside start/end are greyed out; times
// before now + buffer are hidden. A picked value is ISO-like with no
// timezone — the admin side has always treated pickup_datetime as text.

(function () {
  'use strict';

  var STRINGS = {
    en: {
      placeholder: 'Choose date & time',
      chooseDate:  'Select a date to see times',
      noTimes:     'No times available on this date',
      confirm:     'Confirm',
      months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
      dow: ['S','M','T','W','T','F','S'],
      dowFull: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
    },
    fr: {
      placeholder: "Choisir la date et l'heure",
      chooseDate:  'Choisissez une date pour voir les heures',
      noTimes:     'Aucune heure disponible ce jour',
      confirm:     'Confirmer',
      months: ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'],
      dow: ['D','L','M','M','J','V','S'],
      dowFull: ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'],
    },
  };

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  // Normalise a "HH:MM" or "HH:MM:SS" string to minutes-of-day.
  function timeToMinutes(t) {
    if (!t) return null;
    var parts = t.split(':');
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  function minutesToHHMM(mins) {
    return pad2(Math.floor(mins / 60)) + ':' + pad2(mins % 60);
  }

  // ISO date (YYYY-MM-DD) from a Date, in the browser's local timezone.
  function dateToISO(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function isoToDate(iso) {
    var parts = iso.split('-');
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }

  // Compute the list of 30-min slot minutes-of-day for a given local date.
  // Returns an array of { mins, iso } where iso is "YYYY-MM-DDTHH:MM".
  function slotsForDate(dateObj, pickup, now) {
    var dateStr = dateToISO(dateObj);
    if (pickup.start_date && dateStr < pickup.start_date) return [];
    if (pickup.end_date && dateStr > pickup.end_date) return [];
    var dow = dateObj.getDay();

    // Build ranges (minute-of-day pairs) from rules, falling back to
    // operating_hours if no page-specific windows exist.
    var ranges = [];
    var hasRules = pickup.windows && pickup.windows.length > 0;
    if (hasRules) {
      pickup.windows.forEach(function (w) {
        if (!w.days_of_week || w.days_of_week.indexOf(dow) === -1) return;
        var s = timeToMinutes(w.start_time);
        var e = timeToMinutes(w.end_time);
        if (s != null && e != null && e > s) ranges.push([s, e]);
      });
    } else {
      var oh = (pickup.operating_hours || []).find(function (h) {
        return h.day_of_week === dow;
      });
      if (oh) {
        var open = timeToMinutes(oh.open_time);
        var close = timeToMinutes(oh.close_time);
        if (open != null && close != null && close > open + 60) {
          ranges.push([open + 60, close]);
        }
      }
    }

    // Earliest acceptable slot = now + buffer_hours.
    var buffer = (pickup.buffer_hours || 0) * 60 * 60 * 1000;
    var earliest = now.getTime() + buffer;

    var out = [];
    ranges.forEach(function (range) {
      for (var m = range[0]; m < range[1]; m += 30) {
        // Slot datetime in local tz
        var slot = new Date(
          dateObj.getFullYear(),
          dateObj.getMonth(),
          dateObj.getDate(),
          Math.floor(m / 60),
          m % 60,
        );
        if (slot.getTime() < earliest) continue;
        out.push({
          mins: m,
          iso: dateStr + 'T' + minutesToHHMM(m),
          label: formatTime(slot, 'en'),
        });
      }
    });
    return out;
  }

  // Walk forward from `now` looking for the first date that has any slots.
  // Capped at 365 days so a misconfigured page can't spin forever.
  function firstAvailableDate(pickup, now) {
    var cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (var i = 0; i < 365; i++) {
      if (slotsForDate(cursor, pickup, now).length > 0) return cursor;
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
    }
    return null;
  }

  function formatTime(d, locale) {
    return d.toLocaleTimeString(
      locale === 'fr' ? 'fr-CA' : 'en-CA',
      { hour: 'numeric', minute: '2-digit' },
    );
  }

  function formatDisplay(iso, locale) {
    if (!iso) return '';
    var dateStr = iso.split('T')[0];
    var time = iso.split('T')[1] || '';
    var d = isoToDate(dateStr);
    if (time) {
      var parts = time.split(':');
      d.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
    }
    return d.toLocaleString(
      locale === 'fr' ? 'fr-CA' : 'en-CA',
      {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
    );
  }

  function mount(opts) {
    var input = opts.input;
    if (!input) return;
    var locale = opts.locale === 'fr' ? 'fr' : 'en';
    var t = STRINGS[locale];
    var pickup = opts.pickup || {};

    // Wrap input so we can anchor the popover.
    var wrap;
    if (input.parentNode && input.parentNode.classList && input.parentNode.classList.contains('pickup-picker')) {
      wrap = input.parentNode;
    } else {
      wrap = document.createElement('div');
      wrap.className = 'pickup-picker';
      input.parentNode.insertBefore(wrap, input);
      wrap.appendChild(input);
    }

    input.classList.add('pickup-picker__input');
    input.readOnly = true;
    input.placeholder = t.placeholder;
    input.autocomplete = 'off';

    // Display state separate from value: we store ISO, display human.
    function syncDisplay() {
      if (!input.value) return;
      // If the stored value has the display-only marker, skip.
      if (input.dataset.pickerValue && input.dataset.pickerValue === input.value) return;
      // If value is already ISO, derive display; else leave it.
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(input.value)) {
        var display = formatDisplay(input.value, locale);
        input.dataset.pickerIso = input.value;
        input.value = display;
        input.dataset.pickerValue = display;
      }
    }

    // Keep a getter for the ISO on the element so submit code can read it.
    function setSelection(iso) {
      if (iso) {
        input.dataset.pickerIso = iso;
        var display = formatDisplay(iso, locale);
        input.value = display;
        input.dataset.pickerValue = display;
      } else {
        delete input.dataset.pickerIso;
        input.value = '';
        delete input.dataset.pickerValue;
      }
      if (typeof opts.onChange === 'function') opts.onChange(iso || '');
    }

    // Legacy code reads `.value` expecting the submittable value. Patch the
    // property so reads return the ISO while the visible text stays human.
    var protoDesc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    Object.defineProperty(input, 'value', {
      configurable: true,
      get: function () {
        return input.dataset.pickerIso || (protoDesc && protoDesc.get ? protoDesc.get.call(input) : input.getAttribute('value') || '');
      },
      set: function (v) {
        if (!v) {
          setSelection('');
          return;
        }
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
          setSelection(v);
        } else if (protoDesc && protoDesc.set) {
          protoDesc.set.call(input, v);
        }
      },
    });

    // Popover UI
    var pop = document.createElement('div');
    pop.className = 'pickup-picker__popover';
    pop.hidden = true;
    pop.innerHTML = [
      '<div class="pickup-picker__calendar">',
      '  <div class="pickup-picker__calhdr">',
      '    <button type="button" class="pickup-picker__navbtn" data-cal-prev aria-label="Previous month">‹</button>',
      '    <span class="pickup-picker__monthlabel" data-cal-month></span>',
      '    <button type="button" class="pickup-picker__navbtn" data-cal-next aria-label="Next month">›</button>',
      '  </div>',
      '  <div class="pickup-picker__caldow">',
      t.dow.map(function (d) { return '<span>' + d + '</span>'; }).join(''),
      '  </div>',
      '  <div class="pickup-picker__calgrid" data-cal-grid></div>',
      '</div>',
      '<div class="pickup-picker__right">',
      '  <div class="pickup-picker__times" data-cal-times>',
      '    <p class="pickup-picker__times-hint">' + t.chooseDate + '</p>',
      '  </div>',
      '  <div class="pickup-picker__actions">',
      '    <button type="button" class="pickup-picker__confirm" data-cal-confirm disabled>' + t.confirm + '</button>',
      '  </div>',
      '</div>',
    ].join('');
    wrap.appendChild(pop);

    // Draft state. Picks are "draft" until Confirm — outside-click / Escape
    // discards them and the input keeps whatever was committed before.
    var now = new Date();
    var viewMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    var draftDate = null;     // Date instance currently in the slot panel
    var draftSlotIso = '';    // tentative selection, flushed on Confirm

    function render() {
      var monthLabel = pop.querySelector('[data-cal-month]');
      var grid = pop.querySelector('[data-cal-grid]');
      monthLabel.textContent = t.months[viewMonth.getMonth()] + ' ' + viewMonth.getFullYear();
      var firstDow = viewMonth.getDay();
      var daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
      var cellsHtml = '';
      for (var i = 0; i < firstDow; i++) cellsHtml += '<span class="pickup-picker__cell pickup-picker__cell--empty"></span>';
      for (var d = 1; d <= daysInMonth; d++) {
        var cellDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d);
        var slots = slotsForDate(cellDate, pickup, now);
        var available = slots.length > 0;
        var isSelected = draftDate && dateToISO(draftDate) === dateToISO(cellDate);
        var isToday = dateToISO(cellDate) === dateToISO(now);
        var cls = 'pickup-picker__cell';
        if (!available) cls += ' pickup-picker__cell--disabled';
        if (isSelected) cls += ' pickup-picker__cell--selected';
        if (isToday) cls += ' pickup-picker__cell--today';
        cellsHtml += '<button type="button" class="' + cls + '" '
          + (available ? '' : 'disabled ')
          + 'data-cal-day="' + dateToISO(cellDate) + '">' + d + '</button>';
      }
      grid.innerHTML = cellsHtml;
      renderTimes();
      // Confirm becomes active only once a slot is in draft.
      var confirmBtn = pop.querySelector('[data-cal-confirm]');
      confirmBtn.disabled = !draftSlotIso;
    }

    function renderTimes() {
      var times = pop.querySelector('[data-cal-times]');
      if (!draftDate) {
        times.innerHTML = '<p class="pickup-picker__times-hint">' + t.chooseDate + '</p>';
        return;
      }
      var slots = slotsForDate(draftDate, pickup, now);
      if (slots.length === 0) {
        times.innerHTML = '<p class="pickup-picker__times-hint">' + t.noTimes + '</p>';
        return;
      }
      var html = '<div class="pickup-picker__slotgrid">';
      slots.forEach(function (s) {
        var label = formatTime(
          new Date(draftDate.getFullYear(), draftDate.getMonth(), draftDate.getDate(), Math.floor(s.mins / 60), s.mins % 60),
          locale,
        );
        var selected = s.iso === draftSlotIso;
        html += '<button type="button" class="pickup-picker__slot'
          + (selected ? ' pickup-picker__slot--selected' : '') + '"'
          + ' data-slot-iso="' + s.iso + '">' + label + '</button>';
      });
      html += '</div>';
      times.innerHTML = html;
    }

    function open() {
      pop.hidden = false;
      wrap.classList.add('pickup-picker--open');
      now = new Date();
      // Seed draft from the committed value if any; otherwise jump to the
      // first available date so the slot panel is populated immediately.
      var committed = input.dataset.pickerIso || '';
      if (committed) {
        var iso = committed.split('T')[0];
        draftDate = isoToDate(iso);
        draftSlotIso = committed;
        viewMonth = new Date(draftDate.getFullYear(), draftDate.getMonth(), 1);
      } else {
        var first = firstAvailableDate(pickup, now);
        if (first) {
          draftDate = first;
          viewMonth = new Date(first.getFullYear(), first.getMonth(), 1);
        } else {
          draftDate = null;
          viewMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        draftSlotIso = '';
      }
      render();
    }

    // Close without committing. Draft state resets on next open().
    function close() {
      pop.hidden = true;
      wrap.classList.remove('pickup-picker--open');
    }

    function confirmDraft() {
      if (!draftSlotIso) return;
      setSelection(draftSlotIso);
      close();
    }

    // Wire UI events
    input.addEventListener('focus', open);
    input.addEventListener('click', open);
    pop.addEventListener('click', function (e) {
      var tgt = e.target;
      if (!tgt) return;
      if (tgt.matches('[data-cal-prev]')) {
        viewMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1);
        render();
        return;
      }
      if (tgt.matches('[data-cal-next]')) {
        viewMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
        render();
        return;
      }
      if (tgt.matches('[data-cal-day]')) {
        var clicked = isoToDate(tgt.dataset.calDay);
        draftDate = clicked;
        // Keep a draft slot only if it still belongs to the newly-selected day.
        if (draftSlotIso && draftSlotIso.split('T')[0] !== tgt.dataset.calDay) {
          draftSlotIso = '';
        }
        render();
        return;
      }
      if (tgt.matches('[data-slot-iso]')) {
        draftSlotIso = tgt.dataset.slotIso;
        render();
        return;
      }
      if (tgt.matches('[data-cal-confirm]')) {
        confirmDraft();
        return;
      }
    });
    // Check composedPath instead of wrap.contains(e.target): render() swaps
    // innerHTML on date/slot clicks, which detaches the target before this
    // handler runs — wrap.contains would then return false and close the
    // picker even though the click originated inside it. composedPath
    // captures the path at dispatch time.
    document.addEventListener('click', function (e) {
      if (pop.hidden) return;
      var path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.indexOf(wrap) !== -1) return;
      close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.hidden) close();
    });

    // If the input already has a value (e.g. from a prior selection), sync.
    syncDisplay();
  }

  window.ChezGPickupPicker = { mount: mount };
})();
